<template>
    <v-banner
        v-if="showBanner"
        class="tw-z-[1006] tw-px-6"
        color="primary"
        density="compact"
        elevation="24"
        location="bottom left"
        position="fixed">
        <template #text>
            <div class="tw-flex tw-items-center tw-gap-4">
                <v-img
                    class="tw-size-10"
                    src="@/assets/logo.svg"></v-img>
                <span>{{ $t('pwaInstall.text') }}</span>
            </div>
        </template>
        <template #actions>
            <Button
                height="2rem"
                variant="text"
                @click="dismissBanner"
                >{{ $t('pwaInstall.dismiss') }}</Button
            >
            <Button
                class="tw-ml-6"
                color="primary"
                height="2rem"
                variant="flat"
                @click="installPWA"
                >{{ $t('pwaInstall.install') }}</Button
            >
        </template>
    </v-banner>

    <PWAInstallModal v-model="showPwaInstallModal" />
</template>

<script setup lang="ts">
    import { useAppStore } from '@/store/app';
    import { Capacitor } from '@capacitor/core';
    import Button from './shared/Button.vue';
    import PWAInstallModal from './PWAInstallModal.vue';
    import { usePWAInstall } from '@/composables/usePWAInstall';

    const appStore = useAppStore();
    const { showPwaInstallModal, installPWA: triggerPWAInstall } = usePWAInstall();

    const showBanner = computed(() => appStore.getShowPWAInstallBanner);

    let deferredPrompt: any = null;

    // Only show on web platform, not native apps
    const isWebPlatform = !Capacitor.isNativePlatform();

    const checkPWAInstallability = () => {
        if (!isWebPlatform) return;

        // Check if already dismissed
        if (appStore.getPWAInstallDismissed) {
            return;
        }

        // Check if already installed
        if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
            return; // Already installed
        }

        // Check if running in a PWA context (iOS)
        if ((window.navigator as any).standalone || !navigator?.serviceWorker) {
            return; // Already installed (iOS)
        }

        // Check if first visit
        const isFirstVisit = appStore.getIsFirstVisit;
        if (!isFirstVisit) {
            return; // Not first visit
        }

        // Listen for the beforeinstallprompt event
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            deferredPrompt = e;
            appStore.setShowPWAInstallBanner(true);
        });

        // For browsers that don't support beforeinstallprompt
        // Show banner after a delay if conditions are met
        setTimeout(() => {
            if (!deferredPrompt && isFirstVisit && !appStore.getPWAInstallDismissed) {
                appStore.setShowPWAInstallBanner(true);
            }
        }, 3000);
    };

    const installPWA = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const result = await deferredPrompt.userChoice;

            if (result.outcome === 'accepted') {
                appStore.setIsFirstVisit(false);
            }

            deferredPrompt = null;
            appStore.setShowPWAInstallBanner(false);
            appStore.setPWAInstallDismissed(true);
        } else {
            // Use the composable's install function
            await triggerPWAInstall();
            appStore.setShowPWAInstallBanner(false);
            appStore.setPWAInstallDismissed(true);
        }
    };

    const dismissBanner = () => {
        appStore.setShowPWAInstallBanner(false);
        appStore.setPWAInstallDismissed(true);
        appStore.setIsFirstVisit(false); // Mark first visit as complete
    };

    // Initialize on mount
    onMounted(() => {
        checkPWAInstallability();
    });
</script>

<style scoped></style>
